import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { CheckCircle2, ExternalLink, Layers, Loader2, Send, ShieldCheck } from 'lucide-react';
import { db } from '../../utils/firebase';
import type { ChangeOrder, ProposalBuilderData } from '../../types';
import {
  computePhaseCommitAmount,
  getPhaseStatusLabel,
  groupNodeSetsByPhase,
  isPhasedProposal,
} from '../../utils/proposalPhases';
import { subscribeChangeOrdersForDeal } from '../../utils/changeOrders';
import { ensurePhaseTaskGroup } from '../../utils/changeOrderSync';
import { commitPhaseInternally, offerPhaseToClient } from '../../utils/proposalPhaseOffer';
import './PhaseManagementPanel.css';

interface PhaseManagementPanelProps {
  /** proposalBuilders doc id (= dealId). */
  proposalId: string;
  proposal: ProposalBuilderData;
  dealId: string;
  /** timberProjects id when the deal has a Hub project. */
  projectId?: string;
  userId: string;
  userName: string;
  isAdmin: boolean;
  /** Called with the fresh proposal doc after a phase action mutates it. */
  onProposalUpdated?: (proposal: ProposalBuilderData & { id: string }) => void;
}

const fmt = (n: number) => `$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

const toDateSafe = (v: unknown): Date | null => {
  if (v instanceof Date) return v;
  const d = (v as { toDate?: () => Date })?.toDate?.();
  return d instanceof Date ? d : null;
};

/**
 * Staff phase panel for phased proposals: per-phase status, committed value,
 * and the Reprice / Offer to client / internal-commit actions.
 */
const PhaseManagementPanel: React.FC<PhaseManagementPanelProps> = ({
  proposalId,
  proposal,
  dealId,
  projectId,
  userId,
  userName,
  isAdmin,
  onProposalUpdated,
}) => {
  const navigate = useNavigate();
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [busyPhaseId, setBusyPhaseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) return;
    return subscribeChangeOrdersForDeal(dealId, setChangeOrders);
  }, [dealId]);

  // Backfill Hub task groups for committed phases. Public clients can't write
  // taskGroups, so client-signed commits get their group ensured here instead.
  const committedNames = (proposal.phases || [])
    .filter(p => p.status === 'committed')
    .map(p => p.name)
    .join('|');
  useEffect(() => {
    if (!dealId || !committedNames) return;
    committedNames.split('|').forEach(name => {
      void ensurePhaseTaskGroup(dealId, name).catch(() => {
        /* best-effort */
      });
    });
  }, [dealId, committedNames]);

  if (!isPhasedProposal(proposal)) return null;
  const groups = groupNodeSetsByPhase(proposal);
  if (groups.length === 0) return null;

  const refreshProposal = async () => {
    try {
      const snap = await getDoc(doc(db, 'proposalBuilders', proposalId));
      if (snap.exists()) {
        onProposalUpdated?.({ id: snap.id, ...(snap.data() as ProposalBuilderData) });
      }
    } catch {
      /* panel re-renders from parent state; refresh is best-effort */
    }
  };

  const runPhaseAction = async (phaseId: string, action: () => Promise<unknown>) => {
    setBusyPhaseId(phaseId);
    setError(null);
    try {
      await action();
      await refreshProposal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Phase action failed');
    } finally {
      setBusyPhaseId(null);
    }
  };

  const proposalSigned = proposal.status === 'approved' || !!proposal.signature;

  return (
    <div className="phase-mgmt-panel">
      <div className="phase-mgmt-header">
        <Layers size={15} aria-hidden />
        <span>Project phases</span>
        <span className="phase-mgmt-beta">Beta</span>
      </div>
      {error && <p className="phase-mgmt-error">{error}</p>}
      <div className="phase-mgmt-list">
        {groups.map(({ phase, nodeSets }, idx) => {
          const co = phase.changeOrderId
            ? changeOrders.find(c => c.id === phase.changeOrderId)
            : undefined;
          const amount = co ? co.deltaAmount : computePhaseCommitAmount(proposal, phase.id);
          const committedAt = toDateSafe(phase.committedAt);
          const repricedAt = toDateSafe(phase.repricedAt);
          const busy = busyPhaseId === phase.id;
          const committed = phase.status === 'committed';
          // The initial (offered pre-sign) phase commits with the proposal signature —
          // offer/commit CO actions only make sense for later phases or after signing.
          const isInitialUnsigned = !proposalSigned && phase.status === 'offered';

          return (
            <div key={phase.id} className={`phase-mgmt-row status-${phase.status}`}>
              <div className="phase-mgmt-row-main">
                <span className="phase-mgmt-index">Phase {idx + 1}</span>
                <span className="phase-mgmt-name">{phase.name}</span>
                <span className={`phase-mgmt-status status-${phase.status}`}>
                  {phase.status === 'offered' && !proposalSigned
                    ? 'Signs with proposal'
                    : getPhaseStatusLabel(phase.status)}
                </span>
                <span className="phase-mgmt-amount">{fmt(amount)}</span>
              </div>
              <div className="phase-mgmt-row-sub">
                <span className="phase-mgmt-structures">
                  {nodeSets.length > 0 ? nodeSets.map(ns => ns.name).join(', ') : 'No structures assigned'}
                </span>
                {committed && (
                  <span className="phase-mgmt-meta">
                    <CheckCircle2 size={12} aria-hidden /> Committed
                    {phase.committedBy ? ` by ${phase.committedBy}` : ''}
                    {committedAt ? ` · ${committedAt.toLocaleDateString()}` : ''}
                    {co?.reference ? ` · ${co.reference}` : ''}
                  </span>
                )}
                {!committed && repricedAt && (
                  <span className="phase-mgmt-meta">Repriced {repricedAt.toLocaleDateString()}</span>
                )}
              </div>
              {!committed && (
                <div className="phase-mgmt-actions">
                  <button
                    type="button"
                    className="phase-mgmt-btn"
                    onClick={() => navigate(`/proposal-builder/${dealId}`)}
                    title="Adjust this phase's structure pricing in the proposal builder, then offer it to the client"
                  >
                    <ExternalLink size={12} /> Reprice
                  </button>
                  {!isInitialUnsigned && (
                    <>
                      <button
                        type="button"
                        className="phase-mgmt-btn primary"
                        disabled={busy || nodeSets.length === 0}
                        onClick={() =>
                          runPhaseAction(phase.id, () =>
                            offerPhaseToClient({ proposalId, proposal, phaseId: phase.id, userId, projectId }),
                          )
                        }
                        title="Snapshot current pricing into a change order and release this phase for the client to commit on the proposal page"
                      >
                        {busy ? <Loader2 size={12} className="spinning" /> : <Send size={12} />}
                        {phase.status === 'offered' ? 'Re-offer (reprice)' : 'Offer to client'}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="phase-mgmt-btn"
                          disabled={busy || nodeSets.length === 0}
                          onClick={() => {
                            if (!window.confirm(
                              `Record ${phase.name} as committed (${fmt(amount)}) without a client signature? ` +
                              'This posts a change order to the contract ledger.',
                            )) return;
                            void runPhaseAction(phase.id, () =>
                              commitPhaseInternally(
                                { proposalId, proposal, phaseId: phase.id, userId, projectId },
                                { userId, userName, isAdmin },
                              ),
                            );
                          }}
                          title="Admin only — record client approval received outside the portal"
                        >
                          <ShieldCheck size={12} /> Mark committed
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhaseManagementPanel;
